import React, { useState, useCallback } from 'react';
import {
  Smartphone,
  Code,
  Server,
  Zap,
  Download,
  MapPin,
  Briefcase,
  Sparkles,
  Layers,
  Cpu,
  ChevronRight,
} from 'lucide-react';
import { scrollToSection } from '../utils/scroll';
import Reveal from './Reveal';
// eslint-disable-next-line no-unused-vars
import myPic from '../assets/Lokeswaran.png';

const quickFacts = [
  { label: 'Role', value: 'React Native & Full Stack', icon: Briefcase },
  { label: 'Based in', value: 'Chennai, Tamil Nadu, India', icon: MapPin },
  { label: 'Currently at', value: 'SmartZEN Solutions Pvt. Ltd.', icon: Sparkles },
  { label: 'Core Stack', value: 'Mobile · Web · Backend · APIs', icon: Layers },
];

const focusAreas = [
  {
    icon: Smartphone,
    title: 'Mobile App Development',
    desc: 'Feature-rich cross-platform apps with React Native & Expo — native performance, intuitive UI, and smooth deployment on iOS and Android.',
  },
  {
    icon: Code,
    title: 'Web & Frontend',
    desc: 'Responsive, ultra-performant web interfaces using React, JavaScript, TypeScript, and modern CSS — crisp and engaging across all screen sizes.',
  },
  {
    icon: Server,
    title: 'Backend & REST APIs',
    desc: 'Robust, scalable server-side systems with Node.js & Express.js, backed by MySQL, MongoDB, Firebase, and real-time Socket.IO communication.',
  },
  {
    icon: Zap,
    title: 'Integrations & Cloud',
    desc: 'Clean, reliable third-party integrations — Razorpay payments, cloud hosting (GCP, Cloudflare, cPanel), and Google Play & Apple App Store deployment.',
  },
];

const techStack = [
  'React Native', 'React', 'TypeScript', 'JavaScript', 'Node.js',
  'Express.js', 'MySQL', 'MongoDB', 'Firebase', 'REST APIs',
  'Socket.IO', 'Redux', 'Tailwind CSS', 'Bootstrap', 'Git & GitHub',
  'Docker', 'Google Cloud', 'Swagger'
];

export default function About() {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadResume = useCallback(async () => {
    setIsDownloading(true);
    try {
      const link = document.createElement('a');
      link.href = '/resume.pdf';
      link.download = 'Lokeswaran-M.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      window.open('/resume.pdf', '_blank');
    } finally {
      setTimeout(() => setIsDownloading(false), 800);
    }
  }, []);

  const scrollToContact = () => {
    scrollToSection('contact', { behavior: 'smooth' });
  };

  return (
    <section
      id="about"
      className="relative py-24 md:py-32 px-5 sm:px-8 md:px-12 text-gray-900"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-16 pb-8 border-b border-white/60">
          <Reveal delay={150}>
            <div className="inline-block mb-3">
              <div className="border-l-2 border-gray-900 bg-white/60 backdrop-blur-md px-3 py-1.5 shadow-sm">
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-gray-800 font-semibold">
                  About Me
                </span>
              </div>
            </div>
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-display font-extrabold text-gray-900 tracking-tight">
              Crafting Digital Experiences
              <br />
              <span className="text-gray-700">That Deliver Real Value.</span>
            </h2>
          </Reveal>

          <Reveal delay={250} className="max-w-md sm:text-right">
            <p className="text-sm sm:text-base leading-relaxed text-gray-700 font-medium">
              Bridging native mobile performance with resilient full-stack architectures to build scalable, production-ready platforms.
            </p>
          </Reveal>
        </div>

        {/* Bento Grid: Story Card + Authentic Profile Card */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
          {/* Main Story Bento Card */}
          <div className="lg:col-span-8 rounded-3xl glass-card p-7 md:p-10 flex flex-col justify-between relative overflow-hidden">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="p-2 rounded-xl bg-white/60 text-gray-900 border border-white/80 shadow-xs">
                  <Cpu className="w-5 h-5" />
                </span>
                <span className="text-xs uppercase font-mono tracking-widest text-gray-600 font-semibold">
                  Profile Overview
                </span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-display font-bold text-gray-900 mb-4">
                Hi, I'm <span className="text-gray-900 underline underline-offset-4 decoration-gray-400">Lokeswaran M</span>
              </h3>

              <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-6 font-normal">
                A passionate <strong className="text-gray-900 font-semibold">React Native &amp; Full Stack Developer</strong> based in Chennai, India, currently engineering scalable mobile apps and business solutions at <strong className="text-gray-900 font-semibold">SmartZEN Solutions Pvt. Ltd.</strong>
              </p>

              <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-6 font-normal">
                I specialize in transforming complex requirements into high-performance, user-friendly mobile applications and responsive web applications with robust Node.js backend integration, RESTful APIs, and reliable database architecture.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-white/60">
              <button
                onClick={downloadResume}
                disabled={isDownloading}
                className="inline-flex items-center gap-2 rounded-full bg-gray-900 px-5 py-2.5 text-xs sm:text-sm font-medium text-white hover:bg-black transition-colors duration-300 cursor-pointer shadow-md"
              >
                <Download className={`w-4 h-4 ${isDownloading ? 'animate-bounce' : ''}`} />
                <span>{isDownloading ? 'Downloading...' : 'Download Resume (PDF)'}</span>
              </button>
              <button
                onClick={scrollToContact}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/70 bg-white/55 px-5 py-2.5 text-xs sm:text-sm font-medium text-gray-900 hover:bg-white/80 transition-colors duration-300 cursor-pointer shadow-xs"
              >
                <span>Get in Touch</span>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>

          {/* Authentic Profile Card */}
          <div className="lg:col-span-4 rounded-3xl glass-card p-6 flex flex-col items-center text-center justify-between">
            <div className="w-full flex flex-col items-center">
              {/* <div className="relative w-36 h-36 sm:w-44 sm:h-44 rounded-2xl overflow-hidden border border-white/70 mb-5 shadow-[0_4px_24px_rgba(0,0,0,0.08)]">
                <img
                  src={myPic}
                  alt="Lokeswaran M"
className="w-full h-full object-contain"/>
              </div> */}
              <h4 className="text-xl font-bold text-gray-900 mb-1">Lokeswaran M</h4>
              <p className="text-xs font-mono text-gray-600 uppercase tracking-wider mb-3 font-semibold">
                React Native &amp; Full Stack Developer
              </p>
              <p className="text-xs text-gray-700 leading-relaxed max-w-xs mb-4">
                Specializing in cross-platform mobile apps with React Native, modern web frontends, and robust Node.js backends.
              </p>
            </div>

            <button
              onClick={scrollToContact}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white/60 border border-white/75 px-5 py-2.5 text-xs sm:text-sm font-semibold text-gray-900 hover:bg-gray-900 hover:text-white transition-all duration-300 cursor-pointer shadow-xs"
            >
              <span>Connect on Projects</span>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>

        {/* Quick Facts Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {quickFacts.map((fact) => {
            const Icon = fact.icon;
            return (
              <div
                key={fact.label}
                className="rounded-2xl glass-card p-5 flex flex-col justify-between"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-mono uppercase tracking-wider text-gray-600 font-semibold">
                    {fact.label}
                  </span>
                  <Icon className="w-4 h-4 text-gray-700" />
                </div>
                <p className="text-sm sm:text-base font-semibold text-gray-900 leading-snug">
                  {fact.value}
                </p>
              </div>
            );
          })}
        </div>

        {/* Focus Areas: 4 Glass Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {focusAreas.map((area, index) => {
            const Icon = area.icon;
            return (
              <Reveal key={area.title} delay={index * 100}>
                <div className="h-full rounded-2xl glass-card-interactive p-6 flex flex-col justify-between">
                  <div>
                    <div className="w-12 h-12 rounded-xl bg-white/60 flex items-center justify-center mb-5 border border-white/80 text-gray-900 shadow-xs">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      {area.title}
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {area.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Tech Stack Pills */}
        <Reveal delay={200}>
          <div className="rounded-2xl glass-card p-6 sm:p-8">
            <h4 className="text-xs font-mono uppercase tracking-[0.15em] text-gray-600 font-semibold mb-4">
              Core Technical Stack &amp; Technologies
            </h4>
            <div className="flex flex-wrap gap-2">
              {techStack.map((tech) => (
                <span
                  key={tech}
                  className="rounded-full bg-white/60 border border-white/70 px-3.5 py-1.5 text-xs font-medium text-gray-800 hover:bg-white/90 hover:text-black transition-colors shadow-xs"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}